const MS_PER_DAY = 86400000;

const COMPONENT_LABELS = {
  car_rental: 'Car',
  flight: 'Flight',
  hotel: 'Hotel',
  other: 'Other',
};

const datePart = value => (value || '').slice(0, 10);

const components = details => details.components || [];

const NAIVE_DATE = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/;

// Naive `YYYY-MM-DD[THH:mm]` strings carry no zone, so they are read as UTC:
// parsing them as local time makes a range spanning a DST shift gain or lose an hour.
const naiveToUtcMs = value => {
  const parts = NAIVE_DATE.exec(value || '');
  if (!parts) return null;
  const [, year, month, day, hours = 0, minutes = 0] = parts;
  return Date.UTC(year, month - 1, day, hours, minutes);
};

const ceilDays = (from, to) => {
  const start = naiveToUtcMs(from);
  const end = naiveToUtcMs(to);
  if (start === null || end === null) return null;
  const days = Math.ceil((end - start) / MS_PER_DAY);
  return days > 0 ? days : null;
};

const TITLE = {
  car_rental: ({ vehicle = {}, pickup = {} }) =>
    [vehicle.category || vehicle.sampleModel, pickup.locationName]
      .filter(Boolean)
      .join(' · '),
  flight: ({ airline, flightNumber, origin = {}, destination = {} }) => {
    const route =
      origin.locationCode && destination.locationCode
        ? `${origin.locationCode}→${destination.locationCode}`
        : '';
    return [airline || flightNumber, route].filter(Boolean).join(' ');
  },
  hotel: ({ hotelName, city }) => [hotelName, city].filter(Boolean).join(', '),
  package: details => {
    const labels = components(details)
      .map(component => COMPONENT_LABELS[component.type])
      .filter(Boolean);
    return labels.length ? `Package: ${labels.join(' + ')}` : '';
  },
};

export const deriveTitle = (type, details = {}) =>
  TITLE[type] ? TITLE[type](details) : '';

const DATES = {
  car_rental: ({ pickup = {}, dropoff = {} }) => ({
    startDate: datePart(pickup.at),
    endDate: datePart(dropoff.at),
  }),
  flight: ({ departureAt, returnAt }) => ({
    startDate: datePart(departureAt),
    endDate: datePart(returnAt),
  }),
  hotel: ({ checkIn, checkOut }) => ({
    startDate: checkIn || '',
    endDate: checkOut || '',
  }),
  package: details => {
    const ranges = components(details).map(component =>
      // The table and its dispatcher are mutually recursive; resolved at call time.
      // eslint-disable-next-line no-use-before-define
      deriveDates(component.type, component)
    );
    const starts = ranges.map(range => range.startDate).filter(Boolean);
    const ends = ranges.map(range => range.endDate).filter(Boolean);
    return {
      startDate: starts.length ? starts.sort()[0] : '',
      endDate: ends.length ? ends.sort().at(-1) : '',
    };
  },
};

export const deriveDates = (type, details = {}) =>
  DATES[type] ? DATES[type](details) : { startDate: '', endDate: '' };

export const deriveDurations = (type, details = {}) => {
  if (type === 'car_rental') {
    const { duration, ...rest } = details;
    const days = ceilDays(details.pickup?.at, details.dropoff?.at);
    return days ? { ...rest, duration: { days } } : rest;
  }
  if (type === 'hotel') {
    const { nights, ...rest } = details;
    const count = ceilDays(details.checkIn, details.checkOut);
    return count ? { ...rest, nights: count } : rest;
  }
  if (type === 'package') {
    return {
      ...details,
      components: components(details).map(component =>
        deriveDurations(component.type, component)
      ),
    };
  }
  return details;
};
